/**
 * Team Guard
 * Protects routes by verifying team membership
 */

import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTeam } from '@/contexts/TeamContext';
import { isAdminRole } from '@/lib/authz/teamAuthz';
import { Loader2, ShieldAlert, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';

interface TeamGuardProps {
  children: ReactNode;
  requireAdmin?: boolean;
}

export function TeamGuard({ children, requireAdmin = false }: TeamGuardProps) {
  const { t } = useTranslation('common');
  const { user, isLoading: authLoading, logout } = useAuth();
  const {
    activeTeamId,
    loading: teamLoading,
    isMember,
    role,
    error,
    isOnboardingInProgress,
    membershipLoadStatus,
  } = useTeam();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      logger.error('Logout failed:', err);
    }
  };

  // Show loading state while checking auth and team membership
  // FIX: Also show loading if user is in onboarding flow to prevent "Access Denied" flicker
  // Boot stability: Show loading for 'slow' state (not error)
  const isSlowLoading = membershipLoadStatus === 'slow';
  if (authLoading || teamLoading || isOnboardingInProgress || isSlowLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4 max-w-md px-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">
            {isSlowLoading ? 'Dit duurt langer dan normaal…' : t('status.loading')}
          </p>
          {isSlowLoading && (
            <p className="text-xs text-muted-foreground">Even geduld, we laden je gegevens.</p>
          )}
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show error state if there was an error loading team data
  // NOTE: Platform admins without team are handled by the separate /admin/* route tree
  // Boot stability: Only show error if membershipLoadStatus is 'error' (not 'slow')
  if (error && membershipLoadStatus === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4 max-w-md p-6">
          <ShieldAlert className="h-12 w-12 mx-auto text-destructive" />
          <h2 className="text-xl font-semibold">{t('status.errorLoadingTeam')}</h2>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground">{t('status.contactAdmin')}</p>
          <Button onClick={handleLogout} variant="outline" className="mt-4">
            <LogOut className="h-4 w-4 mr-2" />
            {t('actions.signOut')}
          </Button>
        </div>
      </div>
    );
  }

  // Show 403 if user is not a member of the active team
  if (!isMember) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4 max-w-md p-6">
          <ShieldAlert className="h-12 w-12 mx-auto text-destructive" />
          <h2 className="text-xl font-semibold">{t('status.accessDenied')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('status.notTeamMember', { teamId: activeTeamId })}
          </p>
          <p className="text-xs text-muted-foreground">{t('status.requestAccess')}</p>
          <Button onClick={handleLogout} variant="outline" className="mt-4">
            <LogOut className="h-4 w-4 mr-2" />
            {t('actions.signOut')}
          </Button>
        </div>
      </div>
    );
  }

  // Show 403 if admin access is required but user is not admin
  // RBAC V1.2 §9.7: Use centralized helper instead of inline role check
  if (requireAdmin && !isAdminRole(role)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4 max-w-md p-6">
          <ShieldAlert className="h-12 w-12 mx-auto text-destructive" />
          <h2 className="text-xl font-semibold">{t('status.adminRequired')}</h2>
          <p className="text-sm text-muted-foreground">{t('status.adminPrivileges')}</p>
          <p className="text-xs text-muted-foreground">{t('status.currentRole', { role })}</p>
          <Button onClick={handleLogout} variant="outline" className="mt-4">
            <LogOut className="h-4 w-4 mr-2" />
            {t('actions.signOut')}
          </Button>
        </div>
      </div>
    );
  }

  // User is authenticated and authorized - render children
  return <>{children}</>;
}
